import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check } from "lucide-react";
import { Navbar, Footer } from "@/components/landing";
import { Button } from "@/components/ui/button";
import { GUIDE_PAGES, type GuideKey } from "@/config/guides";
import type { GuideContent } from "@/types/guides";
import { trackEvent } from "@/lib/umami";

/**
 * SEO landing page ("guide"). The same copy is server-rendered into the HTML
 * for crawlers (src/server/seo/prerender.ts), so keep everything visible: no
 * collapsed FAQ, no content behind tabs.
 */
export function GuidePage({ guideKey }: { guideKey: GuideKey }) {
  const { t } = useTranslation("guides");
  const guide = t(guideKey, { returnObjects: true }) as unknown as GuideContent;
  const related = Object.entries(GUIDE_PAGES).filter(([, key]) => key !== guideKey);

  return (
    <div className="relative min-h-screen bg-background">
      <Navbar />
      <main>
        <section className="relative overflow-hidden pb-14">
          <div aria-hidden="true" className="absolute inset-0 bg-mesh-low clip-skew md:bg-mesh" />
          <div className="relative mx-auto max-w-[1200px] px-5 pt-32 md:px-10 md:pt-40">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-[13px] font-semibold text-white">
              <span className="size-1.5 rounded-full bg-[#7cf5d0]" />
              {guide.badge}
            </span>
            <h1 className="mt-6 max-w-[860px] text-[40px] leading-[1.04] font-bold tracking-[-0.045em] text-balance text-white sm:text-5xl lg:text-6xl">
              {guide.title}
            </h1>
            <p className="mt-5 max-w-[680px] text-lg leading-relaxed text-[#d5deea]">
              {guide.answer}
            </p>
            <div className="mt-8">
              <Button asChild size="lg" className="shadow-primary">
                <Link
                  to="/signup"
                  onClick={() => trackEvent("signup_cta_click", { location: "guide" })}
                >
                  {guide.cta.button}
                  <ArrowRight />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        <article className="mx-auto max-w-[760px] px-5 pt-12 md:px-0">
          {guide.sections.map((section) => (
            <section key={section.heading} className="pt-10 first:pt-0">
              <h2 className="text-3xl font-bold tracking-[-0.03em]">{section.heading}</h2>
              {section.paragraphs.map((p) => (
                <p key={p} className="mt-4 text-[17px] leading-relaxed text-muted-foreground">
                  {p}
                </p>
              ))}
              {section.points?.length ? (
                <ul className="mt-5 grid gap-3">
                  {section.points.map((point) => (
                    <li key={point} className="flex gap-3 text-[17px] leading-relaxed">
                      <Check className="mt-1.5 size-4 shrink-0 text-protect" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}
        </article>

        <section className="mx-auto max-w-[1200px] px-5 pt-20 md:px-10">
          <h2 className="text-3xl font-bold tracking-[-0.03em]">{guide.comparison.heading}</h2>
          <p className="mt-2 text-muted-foreground">{guide.comparison.intro}</p>
          <div className="mt-8 overflow-x-auto rounded-2xl border bg-card shadow-card">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b">
                  {guide.comparison.columns.map((column, i) => (
                    <th
                      key={column || "label"}
                      scope="col"
                      className={`p-4 font-semibold ${i === guide.comparison.columns.length - 1 ? "text-primary" : ""}`}
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {guide.comparison.rows.map((row) => (
                  <tr key={row.label} className="border-b last:border-b-0">
                    <th scope="row" className="p-4 font-medium">
                      {row.label}
                    </th>
                    {row.cells.map((cell, i) => (
                      <td
                        key={i}
                        className={`p-4 ${i === row.cells.length - 1 ? "font-medium text-foreground" : "text-muted-foreground"}`}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mx-auto max-w-[760px] px-5 pt-20 md:px-0">
          <h2 className="text-3xl font-bold tracking-[-0.03em]">{t("labels.faq")}</h2>
          <dl className="mt-6 divide-y border-y">
            {guide.faq.map((item) => (
              <div key={item.question} className="py-5">
                <dt className="text-[15px] font-semibold">{item.question}</dt>
                <dd className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
                  {item.answer}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="mx-auto max-w-[1200px] px-5 pt-20 md:px-10">
          <div className="relative overflow-hidden rounded-3xl px-7 py-12 bg-mesh md:px-14 md:py-16">
            <div className="relative flex flex-col items-start gap-8">
              <div className="max-w-[560px]">
                <h2 className="text-4xl leading-[1.05] font-bold tracking-[-0.04em] text-white md:text-5xl">
                  {guide.cta.title}
                </h2>
                <p className="mt-4 text-[17px] text-[#d5deea]">{guide.cta.body}</p>
              </div>
              <Button asChild size="lg" className="shadow-primary">
                <Link
                  to="/signup"
                  onClick={() => trackEvent("signup_cta_click", { location: "guide" })}
                >
                  {guide.cta.button}
                  <ArrowRight />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {related.length ? (
          <nav className="mx-auto max-w-[1200px] px-5 pt-16 md:px-10">
            <h2 className="text-sm font-semibold text-muted-foreground">{t("labels.related")}</h2>
            <ul className="mt-3 flex flex-wrap gap-3">
              {related.map(([path, key]) => (
                <li key={path}>
                  <Link
                    to={path}
                    className="inline-flex items-center gap-2 rounded-xl border bg-card px-4 py-3 font-medium shadow-card hover:border-primary"
                  >
                    {t(`${key}.title`)}
                    <ArrowRight className="size-4" />
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}

export default GuidePage;
